/**
 * Epic AB#1824 — tiny `{token}` interpolation helper for i18n templates.
 *
 * The existing LanguageContext exposes `t(key)` → plain template string. Most
 * Devanza surfaces (e.g. `superadmin.categories.reject.reason.counter`) handle
 * substitution with literal `String.replace('{token}', value)` calls inline.
 * The matrix has too many tokens to keep doing that legibly, so we wrap the
 * pattern in one helper.
 *
 * Usage:
 *   const tpl = t('hoursMatrix.subtitle.divergent');
 *   interpolate(tpl, { salonName, n: branchCount, k: divergeCount });
 *
 * Plural variant:
 *   const tpl = pluralize(t, 'hoursMatrix.cell.differsCount', n, { n });
 *
 * The pluralize helper looks up `.one` for n===1 and `.other` for everything
 * else (matches the rest of the codebase's CR-2026-05-15 categories module
 * pattern). Falls back to the base key if neither variant exists.
 */

export function interpolate(
    template: string,
    vars: Record<string, string | number>,
): string {
    let out = template;
    for (const [token, value] of Object.entries(vars)) {
        out = out.split(`{${token}}`).join(String(value));
    }
    return out;
}

export type TFn = (key: string) => string;

/**
 * Pluralize a count-bearing template — `<baseKey>.one` for n===1, `.other`
 * otherwise. Mirrors the ICU MessageFormat shorthand without the full ICU
 * implementation (sufficient for EN/AR singular vs plural).
 */
export function pluralize(
    t: TFn,
    baseKey: string,
    count: number,
    vars: Record<string, string | number>,
): string {
    const suffix = count === 1 ? '.one' : '.other';
    const variantKey = `${baseKey}${suffix}`;
    const tpl = t(variantKey);
    // If the variant key didn't exist, t() echoes the key back. Fall back
    // to the base key so we never render a literal i18n path on screen.
    const safe = tpl === variantKey ? t(baseKey) : tpl;
    return interpolate(safe, vars);
}
